import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Building2, Bell, ShieldCheck, Lock, Database, Globe, Save } from "lucide-react";
import { toast } from "sonner";

const TIMEZONES = ["Asia/Kolkata", "UTC", "Asia/Dubai", "Europe/London", "America/New_York"];

export default function SystemSettings() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings/system").then((r) => setS(r.data)); }, []);

  const save = async () => {
    try {
      await api.put("/settings/system", s);
      toast.success("System settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!s) return <div className="p-10 text-gray-500">Loading…</div>;

  const set = (path, value) => {
    setS((prev) => {
      const next = { ...prev };
      let cur = next;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → System Settings</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">System Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Organisation-wide defaults · GR thresholds · notifications · security</p>
        </div>
        <Button onClick={save} className="f360-gradient border-0" data-testid="settings-save-btn"><Save className="w-4 h-4 mr-1" strokeWidth={2.5} /> Save Changes</Button>
      </div>

      <Tabs defaultValue="organization" className="space-y-4">
        <TabsList className="bg-gray-50 flex-wrap h-auto">
          <TabsTrigger value="organization" data-testid="tab-org"><Building2 className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Organization</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notif"><Bell className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Notifications</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="tab-gr"><ShieldCheck className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> GR Thresholds</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-sec"><Lock className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Security</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data"><Database className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Data &amp; Backup</TabsTrigger>
          <TabsTrigger value="locale" data-testid="tab-locale"><Globe className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Locale</TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="glass rounded-lg p-6 grid grid-cols-2 gap-4">
          <div><Label>Organisation Name</Label><Input value={s.organization?.name || ""} onChange={(e) => set("organization.name", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div><Label>GSTIN</Label><Input value={s.organization?.gstin || ""} onChange={(e) => set("organization.gstin", e.target.value)} placeholder="e.g. 27AAAPL1234C1Z5" className="bg-gray-50 border-gray-200 mt-1" data-testid="settings-gstin" /></div>
          <div className="col-span-2"><Label>Registered Address</Label><Input value={s.organization?.registered_address || ""} onChange={(e) => set("organization.registered_address", e.target.value)} placeholder="e.g. 123, ABC Road, Mumbai - 400001" className="bg-gray-50 border-gray-200 mt-1" data-testid="settings-registered-address" /></div>
          <div><Label>Logo URL</Label><Input value={s.organization?.logo_url || ""} onChange={(e) => set("organization.logo_url", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div><Label>NOC Authority</Label><Input value={s.organization?.noc_authority || ""} onChange={(e) => set("organization.noc_authority", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div className="col-span-2"><Label>Maharashtra DoFES URL</Label><Input value={s.organization?.dofes_link || ""} onChange={(e) => set("organization.dofes_link", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
        </TabsContent>

        <TabsContent value="notifications" className="glass rounded-lg p-6 space-y-4">
          {["critical", "high", "medium", "low"].map((sev) => (
            <div key={sev} className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest text-orange-300 font-bold mb-2">{sev}</div>
              <div className="flex flex-wrap gap-3">
                {["email", "sms", "push", "whatsapp"].map((ch) => {
                  const channels = s.notifications?.channels?.[sev] || [];
                  const on = channels.includes(ch);
                  return (
                    <label key={ch} className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={on} onCheckedChange={(v) => {
                        const next = v ? [...channels, ch] : channels.filter((x) => x !== ch);
                        set(`notifications.channels.${sev}`, next);
                      }} />
                      <span className="text-sm capitalize">{ch}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-orange-300 font-bold mb-3">Escalation Ladder (minutes)</div>
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i}><Label>Step {i + 1}</Label><Input type="number" value={s.notifications?.escalation_minutes?.[i] || 0} onChange={(e) => {
                  const arr = [...(s.notifications?.escalation_minutes || [30, 60, 120])];
                  arr[i] = Number(e.target.value);
                  set("notifications.escalation_minutes", arr);
                }} className="bg-gray-50 border-gray-200 mt-1" /></div>
              ))}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <Switch checked={s.notifications?.quiet_hours?.enabled || false} onCheckedChange={(v) => set("notifications.quiet_hours.enabled", v)} />
              <span className="text-sm font-bold">Quiet hours (suppress non-critical)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="time" value={s.notifications?.quiet_hours?.start || "22:00"} onChange={(e) => set("notifications.quiet_hours.start", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><Label>End</Label><Input type="time" value={s.notifications?.quiet_hours?.end || "07:00"} onChange={(e) => set("notifications.quiet_hours.end", e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="thresholds" className="glass rounded-lg p-6 space-y-4">
          {[
            ["hydrant_min_bar", "Hydrant min pressure (Bar)", 3.5],
            ["sprinkler_min_bar", "Sprinkler min pressure (Bar)", 5.0],
            ["ug_tank_min_pct", "UG fire tank min (%)", 50],
            ["oht_min_pct", "Overhead tank min (%)", 50],
            ["diesel_min_pct", "Diesel day-tank min (%)", 20],
            ["jockey_max_cycles_per_hr", "Jockey cycles/hr alarm", 6],
            ["pump_run_on_seconds", "Pump run-on seconds", 600],
          ].map(([k, label]) => (
            <div key={k}><Label>{label}</Label><Input type="number" step="0.1" value={s.gr_thresholds?.[k] || 0} onChange={(e) => set(`gr_thresholds.${k}`, Number(e.target.value))} className="bg-gray-50 border-gray-200 mt-1" data-testid={`thr-${k}`} /></div>
          ))}
          <div className="text-[11px] text-amber-600 italic bg-amber-50 border border-amber-200 rounded-lg p-3">
            Maharashtra GR No. MFS-2023/C.R.370/UD-14 reference values. Lowering these is allowed only if the licensed agency has an approved variance.
          </div>
        </TabsContent>

        <TabsContent value="security" className="glass rounded-lg p-6 grid grid-cols-2 gap-4">
          <div><Label>Session Timeout (min)</Label><Input type="number" value={s.security?.session_timeout_minutes || 60} onChange={(e) => set("security.session_timeout_minutes", Number(e.target.value))} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div><Label>Password Min Length</Label><Input type="number" value={s.security?.password_min_length || 8} onChange={(e) => set("security.password_min_length", Number(e.target.value))} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div><Label>Failed Login Lockout (attempts)</Label><Input type="number" value={s.security?.failed_login_lockout || 5} onChange={(e) => set("security.failed_login_lockout", Number(e.target.value))} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div className="col-span-2"><Label>IP Allowlist (comma separated CIDR)</Label><Input value={(s.security?.ip_allowlist || []).join(", ")} onChange={(e) => set("security.ip_allowlist", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="bg-gray-50 border-gray-200 mt-1" placeholder="0.0.0.0/0" /></div>
          <div className="col-span-2">
            <Label>Roles requiring 2FA</Label>
            <div className="flex gap-3 mt-2 flex-wrap">
              {["super_admin", "admin", "building_manager", "fire_safety_officer", "viewer"].map((r) => {
                const list = s.security?.require_2fa_roles || [];
                const on = list.includes(r);
                return (
                  <label key={r} className="flex items-center gap-2 cursor-pointer">
                    <Switch checked={on} onCheckedChange={(v) => set("security.require_2fa_roles", v ? [...list, r] : list.filter((x) => x !== r))} />
                    <span className="text-sm">{r}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="data" className="glass rounded-lg p-6 grid grid-cols-2 gap-4">
          <div><Label>Telemetry Retention (days)</Label>
            <Select value={String(s.data?.telemetry_retention_days || 90)} onValueChange={(v) => set("data.telemetry_retention_days", Number(v))}>
              <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="30">30</SelectItem><SelectItem value="90">90</SelectItem><SelectItem value="180">180</SelectItem><SelectItem value="365">365</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Audit Retention (days)</Label><Input type="number" value={s.data?.audit_retention_days || 365} onChange={(e) => set("data.audit_retention_days", Number(e.target.value))} className="bg-gray-50 border-gray-200 mt-1" /></div>
          <div className="col-span-2 flex items-center gap-3 bg-gray-50 rounded-lg p-4">
            <Switch checked={s.data?.nightly_backup || false} onCheckedChange={(v) => set("data.nightly_backup", v)} />
            <div>
              <div className="font-semibold text-sm">Nightly backup</div>
              <div className="text-[11px] text-gray-500">Encrypted dump of MongoDB collections to backup bucket each night at 02:00 IST.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="locale" className="glass rounded-lg p-6 space-y-4">
          <div><Label>Timezone</Label>
            <Select value={s.locale?.timezone || "Asia/Kolkata"} onValueChange={(v) => set("locale.timezone", v)}>
              <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date Format</Label>
            <Select value={s.locale?.date_format || "DD-MMM-YYYY"} onValueChange={(v) => set("locale.date_format", v)}>
              <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                <SelectItem value="DD-MMM-YYYY">DD-MMM-YYYY</SelectItem><SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem><SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Week Starts On</Label>
            <Select value={s.locale?.week_start || "monday"} onValueChange={(v) => set("locale.week_start", v)}>
              <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="monday">Monday</SelectItem><SelectItem value="sunday">Sunday</SelectItem></SelectContent>
            </Select>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
